import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../firebase/config';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import '../styles/Login.css';

const Login = () => {
  const [phoneNumber, setPhoneNumber] = useState('+1');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Convert phone number to email format for Firebase Auth
  // IMPORTANT: Must match iPhone app's phoneToEmail function exactly
  const phoneToEmail = (phone) => {
    // Remove all non-digit characters (same as iPhone app)
    const digits = phone.replace(/\D/g, '');
    // Format as email (Firebase doesn't support phone auth directly in web SDK easily)
    return `${digits}@hotelia.local`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // PhoneInput component should return phone in E.164 format (e.g., +15551234567)
      // Convert to email format for Firebase Auth (must match iPhone app exactly)
      // iPhone app calls: phoneToEmail(phoneNumber) where phoneNumber is the raw input
      const email = phoneToEmail(phoneNumber);
      
      console.log('[Login] Attempting sign in with:', { 
        phoneNumber, 
        authEmail: email 
      });
      
      await signInWithEmailAndPassword(auth, email, password);
      
      // Check if there's a pending invite token - redirect to invite resolver
      const pendingInviteToken = sessionStorage.getItem('pendingInviteToken');
      if (pendingInviteToken) {
        navigate(`/i/${pendingInviteToken}`);
      } else {
        navigate('/home');
      }
    } catch (err) {
      console.error('[Login] Sign in error:', err);
      console.error('[Login] Error code:', err.code);
      console.error('[Login] Error message:', err.message);
      
      // Provide more helpful error messages
      let errorMessage = 'Failed to sign in. Please check your credentials.';
      if (err.code === 'auth/user-not-found') {
        errorMessage = 'No account found with this phone number. Please check your phone number or create an account.';
      } else if (err.code === 'auth/wrong-password') {
        errorMessage = 'Incorrect password. Please try again.';
      } else if (err.code === 'auth/invalid-email') {
        errorMessage = 'Invalid phone number format. Please enter a valid phone number.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h1>Hotelia</h1>
          <p>Sign in to your account</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {error && <div className="error-message">{error}</div>}

          <div className="form-group">
            <label htmlFor="phone">Phone Number</label>
            <PhoneInput
              international
              defaultCountry="US"
              value={phoneNumber}
              onChange={setPhoneNumber}
              className="phone-input"
              id="phone"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              className="input"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          <button type="submit" className="btn btn-primary btn-full" disabled={loading}>
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <div className="auth-footer">
          <p>
            Don't have an account?{' '}
            <Link to="/signup" className="link">
              Create account
            </Link>
          </p>
          <p style={{ marginTop: '1rem' }}>
            <Link to="/create-event" className="link">
              Continue without signing in
            </Link>
          </p>
          
          <div style={{ 
            marginTop: '2.5rem', 
            paddingTop: '1.5rem', 
            borderTop: '2px solid var(--border-color)', 
            textAlign: 'center',
            backgroundColor: 'var(--background-alt)',
            marginLeft: '-2.5rem',
            marginRight: '-2.5rem',
            marginBottom: '-2.5rem',
            paddingLeft: '2.5rem',
            paddingRight: '2.5rem',
            paddingBottom: '1.5rem',
            borderRadius: '0 0 var(--radius-xl) var(--radius-xl)'
          }}>
            <div style={{ 
              fontSize: '1rem', 
              marginBottom: '1rem',
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: '1rem',
              alignItems: 'center'
            }}>
              <a href="/terms.html" className="link" style={{ 
                color: 'var(--primary-color)',
                textDecoration: 'underline',
                fontWeight: '600',
                fontSize: '0.95rem'
              }}>
                Terms of Service
              </a>
              <span style={{ color: 'var(--text-secondary)', fontSize: '1.2rem' }}>•</span>
              <a href="/privacy.html" className="link" style={{ 
                color: 'var(--primary-color)',
                textDecoration: 'underline',
                fontWeight: '600',
                fontSize: '0.95rem'
              }}>
                Privacy Policy
              </a>
              <span style={{ color: 'var(--text-secondary)', fontSize: '1.2rem' }}>•</span>
              <a href="/sms-consent.html" className="link" style={{ 
                color: 'var(--primary-color)',
                textDecoration: 'underline',
                fontWeight: '600',
                fontSize: '0.95rem'
              }}>
                SMS Consent
              </a>
              <span style={{ color: 'var(--text-secondary)', fontSize: '1.2rem' }}>•</span>
              <Link 
                to="/support"
                className="link"
                style={{ 
                  color: 'var(--primary-color)',
                  textDecoration: 'underline',
                  fontWeight: '600',
                  fontSize: '0.95rem'
                }}
              >
                Support
              </Link>
            </div>
            <div style={{ marginTop: '1.5rem' }}>
              <a 
                href="https://apps.apple.com/us/app/hotelia-live/id6757988326" 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ 
                  display: 'inline-block',
                  textDecoration: 'none'
                }}
              >
                <img 
                  src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=250x83&releaseDate=1735689600" 
                  alt="Download on the App Store"
                  style={{
                    height: '60px',
                    width: 'auto',
                    display: 'block',
                    margin: '0 auto'
                  }}
                />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;

